import { Button } from '@equitymultiple/react-eui';
import { graphql, HeadFC, PageProps } from 'gatsby';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import parse from 'html-react-parser';
import React from 'react';

import Accordion from '../../components/Accordion/Accordion';
import Carousel from '../../components/Carousel/Carousel';
import Contact from '../../components/Contact/Contact';
import Layout from '../../components/layout';
import { SharedHead } from '../../components/SharedHead/SharedHead';
import SvgProjector from '../../components/SvgProjector/SvgProjector';
import { Breakpoints } from '../../constants';
import useWindowSize from '../../hooks/useWindowSize';
import { capitalize } from '../../utils/formatters';
import * as styles from './KeepEarnGrowFade.module.scss';
import { KeepEarnGrowFadeImagesStatic } from './KeepEarnGrowFadeBackgroundImages';

const KeepEarnGrowFade: React.FC<PageProps<Queries.KeepEarnGrowFadeQuery>> = ({
  data,
}) => {
  const { keepEarnGrowLandingFade } = data.post
    ?.template as Queries.KeepEarnGrowLandingFadeFragment;

  const offerings = (
    data.post?.template as Queries.KeepEarnGrowLandingFadeFragment
  )?.offerings?.propertyOfferingsRepeater;

  const {
    investmentType,
    heroHeader,
    heroInvestmentTypeDescription,
    heroPoints,
    belowTheFoldInvestmentTypeDescription,
    faqs,
  } = keepEarnGrowLandingFade;

  const { isWiderThan } = useWindowSize();

  return (
    <Layout>
      <div
        className={`${styles.keepEarnGrowFade} ${
          styles[investmentType as 'keep' | 'earn' | 'grow']
        }`}
      >
        <div className={styles.backgroundImageContainer}>
          <KeepEarnGrowFadeImagesStatic
            investmentType={investmentType as 'keep' | 'earn' | 'grow'}
            className={styles.heroBackgroundImage}
          />
        </div>
        <div className={styles.backgroundImageFade} />
        <div className="container">
          <div className={styles.hero}>
            <div className={styles.largeHeroHeader}>
              <SvgProjector
                className={styles.heroKegIcon}
                fileUrl={`icons/${investmentType as 'keep' | 'earn' | 'grow'}`}
              />
              <h1 className="heading1 expressive">
                {capitalize(investmentType)}
              </h1>
            </div>

            <div className="row">
              <div className="col-lg-6">
                <h3 className="heading3 expressive">{heroHeader}</h3>
                <p className="body1">
                  {parse(heroInvestmentTypeDescription || '')}
                </p>
              </div>
              <div className={styles.heroVerticalSpace} />
              <div className={`col-lg-6 ${styles.rightColPadding}`}>
                <div className={styles.heroDataPairsContainer}>
                  {heroPoints.length > 0 &&
                    heroPoints.map(({ label, value }) => {
                      return (
                        <>
                          <div className={styles.heroDataPair}>
                            <span className="disclaimer">{label}</span>
                            <span className="body1 emphasis">{value}</span>
                          </div>
                        </>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.carouselSection}>
            <div>
              <span className="overline">Representative Investments</span>
            </div>
            <div className="row">
              <div className="col-lg-6">
                <div className={styles.propertiesCarouselWrapper}>
                  <Carousel
                    slidesPerView={1}
                    leftFade={false}
                    rightFade={false}
                    loop
                    controlsPlacement={
                      isWiderThan(Breakpoints.Lg) ? 'right' : 'bottom'
                    }
                    slides={
                      offerings?.map((offering) => {
                        const {
                          offeringLink,
                          additionalLabel1,
                          additionalValue1,
                          additionalLabel2,
                          additionalValue2,
                          additionalLabel3,
                          additionalValue3,
                          propertyImage,
                          propertyName,
                          totalDealSize,
                          dealSizeLabel,
                        } = offering;
                        return (
                          <a
                            className={styles.propertyLink}
                            key={offeringLink}
                            href={offeringLink || ''}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <h3 className="heading3 expressive">
                              {propertyName}
                            </h3>
                            <div className={styles.propertyPhotoFrame}>
                              <div className={styles.irrTag}>
                                <span className="body2 light">
                                  {dealSizeLabel}{' '}
                                </span>
                                <span className="body1 emphasis">
                                  {totalDealSize}
                                </span>
                              </div>
                              <GatsbyImage
                                alt={propertyImage?.altText || ''}
                                image={
                                  propertyImage?.gatsbyImage as IGatsbyImageData
                                }
                              />
                            </div>
                            <div className={styles.propertiesData}>
                              <div className={styles.propertiesDataWrapper}>
                                <div className={styles.dataPairNoWrap}>
                                  <div className={styles.dataPair}>
                                    <p className="disclaimer">
                                      {additionalLabel1}
                                    </p>
                                    <p className="body1 nowrap">
                                      {additionalValue1}
                                    </p>
                                  </div>
                                  <div className={styles.dataPair}>
                                    <p className="disclaimer">
                                      {additionalLabel2}
                                    </p>
                                    <p className="body1 nowrap">
                                      {additionalValue2}
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className={`${styles.dataPair} ${styles.lastDataPair}`}
                                >
                                  <p className="disclaimer">
                                    {additionalLabel3}
                                  </p>
                                  <p className="body1 nowrap">
                                    {additionalValue3}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </a>
                        );
                      }) || []
                    }
                  />
                </div>
                <Button
                  className={styles.carouselCta}
                  href="https://app.equitymultiple.com/users/sign_in"
                >
                  Invest Now
                </Button>
              </div>
              <div className={`col-lg-6 ${styles.rightColPadding}`}>
                <p className="body1">
                  {parse(belowTheFoldInvestmentTypeDescription || '')}
                </p>
                {faqs.length > 0 &&
                  faqs.map((faq) => (
                    <Accordion summary={faq.question} key={faq.question}>
                      <p className="body1">{parse(faq.answer || '')}</p>
                    </Accordion>
                  ))}
              </div>
            </div>
          </div>
        </div>
        <Contact />
      </div>
    </Layout>
  );
};

export const Head: HeadFC<Queries.KeepEarnGrowFadeQuery> = ({
  location,
  data,
}) => {
  return <SharedHead data={data} location={location} />;
};

export default KeepEarnGrowFade;

export const pageQuery = graphql`
  fragment KeepEarnGrowLandingFade on WpTemplate_KeepEarnGrowFade {
    keepEarnGrowLandingFade {
      investmentType
      heroHeader
      heroInvestmentTypeDescription
      belowTheFoldInvestmentTypeDescription
      heroPoints {
        label
        value
      }
      faqs {
        question
        answer
      }
    }
    offerings {
      propertyOfferingsRepeater {
        dealSizeLabel
        totalDealSize
        propertyName
        offeringLink
        additionalLabel1
        additionalValue1
        additionalLabel2
        additionalValue2
        additionalLabel3
        additionalValue3
        propertyImage {
          gatsbyImage(width: 720, placeholder: BLURRED)
          altText
        }
      }
    }
  }
  query KeepEarnGrowFade(
    # these variables are passed in via createPage.pageContext in gatsby-node.js
    $id: String!
  ) {
    # selecting the current post by id
    post: wpPage(id: { eq: $id }) {
      seo {
        ...SeoFragment
      }
      ldJsonSchema {
        multipleSchemas {
          schemaStringContent
        }
        schemaStringContent
      }
      id
      title
      uri
      content
      template {
        ...KeepEarnGrowLandingFade
      }
    }
  }
`;
